import { useEffect, useRef, useState } from "react";
import api from "../lib/typedApi";
import { useAuth } from "../lib/auth";
import { PageHeader, NBInput, NBButton, Avatar } from "../components/UI";
import { Send, Wifi, WifiOff, Hash, Plus, X, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [members, setMembers] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newMode, setNewMode] = useState("channel"); // 'channel' | 'dm'
  const [newName, setNewName] = useState("");
  const [newDmId, setNewDmId] = useState("");
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const myId = user?.member?.id;
  const token = localStorage.getItem("familyos_token");

  const loadThreads = async () => {
    const [t, m] = await Promise.all([api.get("/api/threads"), api.get("/api/members")]);
    setThreads(t.data);
    setMembers(m.data);
    if (!activeId && t.data.length > 0) setActiveId(t.data[0].id);
  };
  useEffect(() => { loadThreads(); }, []);

  const loadMessages = async (threadId) => {
    if (!threadId) return;
    const { data } = await api.get(`/api/threads/${threadId}/messages`);
    setMessages(data);
  };
  useEffect(() => { loadMessages(activeId); }, [activeId]);

  useEffect(() => {
    if (!token) return;
    let reconnectTimer = null;
    const connect = () => {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${proto}//${window.location.host}/api/ws/chat?token=${token}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (evt) => {
        try {
          const m = JSON.parse(evt.data);
          if (!m?.id) return;
          // Only append if it belongs to the active thread
          if (m.thread_id === activeId || (!m.thread_id && threads.find((t) => t.id === activeId)?.is_default)) {
            setMessages((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
          }
          // Refresh thread list to bump last-message previews
          loadThreads();
        } catch {}
      };
      ws.onclose = () => { setConnected(false); reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
    };
    connect();
    const hb = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send("ping");
    }, 25000);
    return () => {
      clearInterval(hb);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [token, activeId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    const content = text;
    setText("");
    try {
      wsRef.current?.send(JSON.stringify({ content, thread_id: activeId }));
    } catch {
      setText(content);
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      const payload = newMode === "dm"
        ? { name: members.find((m) => m.id === newDmId)?.name || "DM", type: "dm", member_ids: [myId, newDmId].filter(Boolean) }
        : { name: newName.trim(), type: "channel", member_ids: [] };
      const { data } = await api.post("/api/threads", payload);
      setNewOpen(false); setNewName(""); setNewDmId("");
      await loadThreads();
      setActiveId(data.id);
      toast.success(newMode === "dm" ? "DM ready" : "Channel created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const memberById = (id) => members.find((m) => m.id === id);
  const activeThread = threads.find((t) => t.id === activeId);
  const dmPartner = activeThread?.type === "dm"
    ? memberById((activeThread.member_ids || []).find((id) => id !== myId))
    : null;

  return (
    <div data-testid="messages-page" className="flex flex-col" style={{ height: "calc(100vh - 80px)" }}>
      <PageHeader
        title="Family Chat"
        subtitle="Channels and direct messages — all real-time."
        color="#E9B5A0"
        action={
          <span className="nb-badge inline-flex items-center gap-1.5" data-testid="ws-status"
            style={{ background: connected ? "var(--hh-sage-soft)" : "var(--hh-peach)" }}>
            {connected ? <Wifi strokeWidth={2.4} className="w-3 h-3" /> : <WifiOff strokeWidth={2.4} className="w-3 h-3" />}
            {connected ? "Live" : "Reconnecting…"}
          </span>
        }
      />

      <div className="nb-card flex-1 flex overflow-hidden !p-0">
        {/* Threads sidebar */}
        <aside className="w-64 shrink-0 flex flex-col" style={{ borderRight: "1px solid var(--hh-line)", background: "var(--hh-card-soft)" }} data-testid="threads-sidebar">
          <div className="p-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--hh-line)" }}>
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--hh-ink-soft)" }}>Threads</span>
            <button onClick={() => setNewOpen(true)} className="nb-btn p-1.5" data-testid="new-thread-btn" title="New thread">
              <Plus strokeWidth={2.4} className="w-3.5 h-3.5" />
            </button>
          </div>
          <ul className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
            {threads.map((t) => {
              const partner = t.type === "dm" ? memberById((t.member_ids || []).find((id) => id !== myId)) : null;
              const active = t.id === activeId;
              return (
                <li key={t.id}>
                  <button
                    onClick={() => setActiveId(t.id)}
                    data-testid={`thread-${t.id}`}
                    className="w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    style={active ? { background: "var(--hh-card)", border: "1px solid var(--hh-line)" } : {}}
                  >
                    {t.type === "dm"
                      ? (partner ? <Avatar name={partner.name} color={partner.avatar_color} size={26} /> : <MessageSquare strokeWidth={2.2} className="w-4 h-4" />)
                      : <Hash strokeWidth={2.4} className="w-4 h-4" style={{ color: "var(--hh-terracotta)" }} />
                    }
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{t.type === "dm" ? (partner?.name || t.name) : t.name}</div>
                      {t.last_message && <div className="text-[11px] truncate" style={{ color: "var(--hh-ink-soft)" }}>{t.last_message}</div>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Active thread pane */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: "1px solid var(--hh-line)" }}>
            {activeThread?.type === "dm"
              ? (dmPartner && <Avatar name={dmPartner.name} color={dmPartner.avatar_color} size={28} />)
              : <Hash strokeWidth={2.4} className="w-5 h-5" style={{ color: "var(--hh-terracotta)" }} />}
            <h2 className="font-extrabold text-lg" style={{ fontFamily: "var(--hh-display)" }}>
              {activeThread?.type === "dm" ? (dmPartner?.name || "DM") : activeThread?.name || "—"}
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <p style={{ color: "var(--hh-ink-soft)" }}>No messages yet. Say hi!</p>
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === myId;
              return (
                <div key={m.id} className={`flex gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                  {!mine && <Avatar name={m.sender_name} color={m.sender_color || "#E8B96B"} size={32} />}
                  <div className="max-w-[70%] rounded-2xl px-4 py-2"
                       style={{ background: mine ? "var(--hh-honey-soft)" : "var(--hh-card)", border: "1px solid var(--hh-line)" }}>
                    {!mine && <p className="text-xs font-bold mb-1" style={{ fontFamily: "var(--hh-display)" }}>{m.sender_name}</p>}
                    <p className="text-sm break-words">{m.content}</p>
                    <p className="text-[10px] mt-1" style={{ color: "var(--hh-ink-soft)" }}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                  {mine && <Avatar name={m.sender_name} color={m.sender_color || "#E8B96B"} size={32} />}
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="p-3 flex gap-2" style={{ borderTop: "1px solid var(--hh-line)" }}>
            <NBInput testId="message-input" placeholder={activeThread?.type === "dm" ? `Message ${dmPartner?.name || ""}…` : "Type a message…"} value={text} onChange={(e) => setText(e.target.value)} />
            <NBButton testId="send-message" type="submit" color="#E9B5A0">
              <Send strokeWidth={2.4} className="w-4 h-4" />
            </NBButton>
          </form>
        </div>
      </div>

      {newOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setNewOpen(false)} data-testid="new-thread-modal">
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>New thread</h2>
              <button onClick={() => setNewOpen(false)} className="nb-btn p-1.5"><X strokeWidth={2.4} className="w-4 h-4" /></button>
            </div>
            <div className="flex gap-2 mb-4 p-1 rounded-full" style={{ background: "var(--hh-card-soft)", border: "1px solid var(--hh-line)" }}>
              {["channel", "dm"].map((m) => (
                <button key={m} type="button" onClick={() => setNewMode(m)} data-testid={`new-${m}`}
                  className="flex-1 py-1.5 px-3 rounded-full text-xs font-semibold capitalize transition-colors"
                  style={newMode === m ? { background: "var(--hh-ink)", color: "var(--hh-card)" } : { color: "var(--hh-ink-soft)" }}>
                  {m === "dm" ? "Direct message" : "# Channel"}
                </button>
              ))}
            </div>
            <form onSubmit={submitNew} className="space-y-3">
              {newMode === "channel" ? (
                <NBInput testId="new-thread-name" required placeholder="Channel name (e.g. weekend-plans)" value={newName} onChange={(e) => setNewName(e.target.value)} />
              ) : (
                <select required value={newDmId} onChange={(e) => setNewDmId(e.target.value)} className="nb-input w-full" data-testid="new-thread-dm-target">
                  <option value="">Select a member…</option>
                  {members.filter((m) => m.id !== myId).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              )}
              <NBButton testId="submit-new-thread" type="submit" color="#E9B5A0" className="w-full">Create</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
